import React from 'react';
import Modal from '../ui/Modal';
import { getSeverityStyle } from '../../utils/licenseErrorHandler';

const FeatureUpgradeModal = ({ isOpen, onClose, licenseError }) => {
    if (!licenseError || !isOpen) return null;

    const severityStyle = getSeverityStyle(licenseError.severity || 'info');

    const handleUpgrade = () => {
        if (licenseError.actionUrl) {
            window.open(licenseError.actionUrl, '_blank');
        }
        onClose();
    };

    const getFeatureIcon = (feature) => {
        const icons = {
            analytics: '📊',
            campaigns: '📢',
            botFlows: '🤖',
            templates: '📝',
            apiAccess: '🔗',
            mediaUpload: '📁',
            advancedReports: '📈',
            multiTenant: '🏢',
            customIntegrations: '🔧',
            prioritySupport: '🎧'
        };
        return icons[feature] || '✨';
    };

    const getPlanBadgeColor = (plan) => {
        const colors = {
            dev: 'bg-gray-100 text-gray-800',
            basic: 'bg-blue-100 text-blue-800',
            professional: 'bg-purple-100 text-purple-800',
            enterprise: 'bg-green-100 text-green-800'
        };
        return colors[plan] || 'bg-gray-100 text-gray-800';
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center mb-6">
                    <div className={`w-12 h-12 rounded-full ${severityStyle.bgColor} ${severityStyle.borderColor} border-2 flex items-center justify-center mr-4`}>
                        <span className="text-2xl">{getFeatureIcon(licenseError.feature)}</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {licenseError.title || 'Feature Not Available'}
                        </h3>
                        <p className="text-sm text-gray-600">
                            Upgrade your plan to access this feature
                        </p>
                    </div>
                </div>

                {/* Feature Information */}
                <div className={`${severityStyle.bgColor} ${severityStyle.borderColor} border rounded-lg p-4 mb-6`}>
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">Requested Feature</span>
                        <span className={`text-sm font-semibold ${severityStyle.textColor} capitalize`}>
                            {licenseError.feature || 'Advanced Feature'}
                        </span>
                    </div>
                    
                    {licenseError.details && (
                        <p className="text-sm text-gray-700 mb-3">
                            {licenseError.details}
                        </p>
                    )}

                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-gray-600">Current Plan</p>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPlanBadgeColor(licenseError.currentPlan)}`}>
                                {licenseError.currentPlan || 'Free'}
                            </span>
                        </div>
                        {licenseError.requiredPlan && (
                            <div className="text-right">
                                <p className="text-xs text-gray-600">Required Plan</p>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPlanBadgeColor(licenseError.requiredPlan)}`}>
                                    {licenseError.requiredPlan}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* License Features Overview */}
                {licenseError._license?.features && (
                    <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Available Features</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {Object.entries(licenseError._license.features).map(([feature, enabled]) => (
                                <div
                                    key={feature}
                                    className={`p-3 rounded-md border ${
                                        enabled 
                                            ? 'bg-green-50 border-green-200' 
                                            : 'bg-gray-50 border-gray-200'
                                    }`}
                                >
                                    <div className="flex items-center">
                                        <span className="text-lg mr-2">{getFeatureIcon(feature)}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-medium truncate ${
                                                enabled ? 'text-green-800' : 'text-gray-500'
                                            }`}>
                                                {feature.replace(/([A-Z])/g, ' $1').trim()}
                                            </p>
                                            <div className="flex items-center mt-1">
                                                {enabled ? (
                                                    <>
                                                        <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="text-xs text-green-600">Available</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg className="w-3 h-3 text-gray-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="text-xs text-gray-500">Upgrade Required</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Plan Information */}
                {(licenseError.currentPlan || licenseError._license?.plan) && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Current License</p>
                                <p className="text-lg font-semibold text-gray-900 capitalize">
                                    {licenseError.currentPlan || licenseError._license?.plan}
                                </p>
                            </div>
                            {licenseError._license?.blockingLevel !== undefined && (
                                <div className="text-right">
                                    <p className="text-xs text-gray-600">Blocking Level</p>
                                    <p className={`text-sm font-semibold ${
                                        licenseError._license.blockingLevel === 0 ? 'text-green-600' :
                                        licenseError._license.blockingLevel === 1 ? 'text-yellow-600' :
                                        licenseError._license.blockingLevel === 2 ? 'text-orange-600' :
                                        'text-red-600'
                                    }`}>
                                        Level {licenseError._license.blockingLevel}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Help Message */}
                {licenseError.help && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start">
                            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm text-blue-800">{licenseError.help}</p>
                        </div>
                    </div>
                )}

                {/* Upgrade Benefits */}
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">✨ Upgrade Benefits</h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-center">
                            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Access to advanced features and analytics
                        </li>
                        <li className="flex items-center">
                            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Higher usage quotas and limits
                        </li>
                        <li className="flex items-center">
                            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Priority support and assistance
                        </li>
                        <li className="flex items-center">
                            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Unlock full platform potential
                        </li>
                    </ul>
                </div>

                {/* Actions */}
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Continue with Current Plan
                    </button>
                    
                    {licenseError.actionUrl && (
                        <button
                            onClick={handleUpgrade}
                            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${severityStyle.buttonColor}`}
                        >
                            {licenseError.action || 'Upgrade Plan'}
                        </button>
                    )}
                </div>

                {/* Timestamp */}
                {licenseError.timestamp && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-500 text-center">
                            Feature requested at {new Date(licenseError.timestamp).toLocaleString()}
                        </p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default FeatureUpgradeModal;